<template>
  <el-dialog
    :title="`商户价配置 - ${priceBook ? priceBook.name : ''}`"
    :visible.sync="dialogVisible"
    width="80%"
    top="5vh"
    append-to-body
    :close-on-click-modal="false"
    @closed="handleClosed"
  >
    <div v-if="priceBook" class="book-info">
      <el-tag size="small">{{ projectTypeText }}</el-tag>
      <span v-if="isCeiling" class="book-info-tip">吊顶价盘：扣板、电器按产品型号分别定价，辅材不配置</span>
      <span v-else class="book-info-tip">商户单价留空时使用价盘标准价；生效调价任务会覆盖当前生效价，不回写商户单价</span>
    </div>

    <!-- 搜索栏 -->
    <div class="filter-bar">
      <el-input
        v-model="searchName"
        placeholder="请输入商户名称"
        size="small"
        clearable
        style="width: 220px"
        @keyup.enter.native="handleSearch"
      />
      <el-button type="primary" size="small" icon="el-icon-search" @click="handleSearch">查询</el-button>
      <el-button size="small" icon="el-icon-refresh" @click="handleReset">重置</el-button>
      <div class="filter-bar-right">
        <el-button
          size="small"
          icon="el-icon-download"
          :loading="templateLoading"
          @click="handleDownloadTemplate"
        >下载模板</el-button>
        <el-button type="warning" size="small" icon="el-icon-upload2" plain @click="handleOpenImport">批量导入</el-button>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAdd">批量新增商户价</el-button>
        <el-button
          type="warning"
          size="small"
          icon="el-icon-document-checked"
          :disabled="!pendingChangeCount"
          @click="openPendingDialog"
        >待提交审核{{ pendingChangeCount ? `（${pendingChangeCount}）` : '' }}</el-button>
        <el-button
          type="danger"
          size="small"
          icon="el-icon-delete"
          plain
          :loading="batchDeleteLoading"
          :disabled="!selectedRows.length"
          @click="handleBatchDelete"
        >批量删除商户</el-button>
      </div>
    </div>

    <!-- 暂存的商户价调整 -->
    <el-dialog
      title="待提交的价盘调价"
      :visible.sync="pendingDialogVisible"
      width="760px"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-alert
        title="以下操作尚未提交审批，提交后将合并为一张审批单，审批通过后统一生效。"
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom: 12px"
      />
      <el-table
        ref="pendingTable"
        :data="pendingPageRows"
        row-key="_pendingIndex"
        border
        stripe
        size="small"
        max-height="360"
        @selection-change="handlePendingSelectionChange"
      >
        <el-table-column type="selection" width="45" align="center" />
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="merchantName" label="商户名称" min-width="140" show-overflow-tooltip />
        <el-table-column v-if="isCeiling" prop="goodsFilterName" label="产品型号" min-width="120" show-overflow-tooltip />
        <el-table-column v-if="!isCeiling" prop="pricingItemName" label="计价项" min-width="120" show-overflow-tooltip />
        <el-table-column label="操作" width="90" align="center">
          <template slot-scope="scope">{{ pendingActionText(scope.row.action) }}</template>
        </el-table-column>
        <el-table-column label="单价" width="100" align="right">
          <template slot-scope="scope">{{ hasMerchantPrice(scope.row.price) ? scope.row.price : '使用标准价' }}</template>
        </el-table-column>
        <el-table-column v-if="isCeiling" label="保底补贴单价" width="120" align="right">
          <template slot-scope="scope">
            {{ isCeilingPanelFilter(scope.row.goodsFilterId)
              ? (hasMerchantPrice(scope.row.subsidyPrice) ? scope.row.subsidyPrice : '使用型号标准价')
              : '-' }}
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="scope">{{ pendingStatusText(scope.row.status) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="130" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.action === 'ADD'"
              type="text"
              size="small"
              icon="el-icon-edit"
              @click="editPendingChange(scope.row._pendingIndex)"
            >编辑</el-button>
            <el-button type="text" size="small" icon="el-icon-delete" @click="removePendingChange(scope.row._pendingIndex)">移除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div v-if="pendingChangeCount > pendingPage.pageSize" class="pending-pagination">
        <el-pagination
          small
          background
          layout="total, prev, pager, next"
          :total="pendingChangeCount"
          :current-page="pendingPage.currentPage"
          :page-size="pendingPage.pageSize"
          @current-change="handlePendingPageChange"
        />
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="pendingDialogVisible = false">关闭</el-button>
        <el-button
          type="danger"
          plain
          :disabled="!pendingSelectedRows.length"
          @click="removeSelectedPendingChanges"
        >批量移除{{ pendingSelectedRows.length ? `（${pendingSelectedRows.length}）` : '' }}</el-button>
        <el-button
          type="primary"
          :disabled="!pendingChangeCount"
          :loading="pendingSubmitting"
          @click="submitPendingChanges"
        >提交审核{{ pendingChangeCount ? `（${pendingChangeCount} 条）` : '' }}</el-button>
      </span>
    </el-dialog>

    <!-- 商户汇总表格 -->
    <el-table
      v-loading="tableLoading"
      :data="tableData"
      row-key="merchantId"
      border
      stripe
      size="small"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="45" align="center" />
      <el-table-column type="index" label="序号" width="60" align="center" />
      <el-table-column prop="merchantName" label="商户名称" min-width="220" show-overflow-tooltip />
      <el-table-column prop="merchantPhone" label="商户电话" min-width="150" />
      <el-table-column label="价格明细" width="130" align="center">
        <template slot-scope="scope">
          <span>{{ scope.row.detailCount || 0 }} 项</span>
        </template>
      </el-table-column>
      <el-table-column label="审批状态" min-width="200" align="center">
        <template slot-scope="scope">
          <el-tag v-if="summaryPendingChangeCount(scope.row)" type="warning" size="small">
            待提交 {{ summaryPendingChangeCount(scope.row) }} 项
          </el-tag>
          <el-tag v-if="scope.row.pendingApprovalCount" type="primary" size="small">
            审批中 {{ scope.row.pendingApprovalCount }} 项
          </el-tag>
          <span
            v-if="!summaryPendingChangeCount(scope.row) && !scope.row.pendingApprovalCount"
            class="empty-text"
          >-</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="130" align="center" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" size="small" icon="el-icon-view" @click="openMerchantDetails(scope.row)">
            查看明细
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pager-bar">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="page.total"
        :current-page="page.currentPage"
        :page-size="page.pageSize"
        :page-sizes="[10, 20, 50]"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </div>

    <!-- 商户价格明细弹窗 -->
    <el-dialog
      :title="`商户价明细 - ${detailMerchant ? detailMerchant.merchantName : ''}`"
      :visible.sync="detailDialogVisible"
      width="92%"
      top="4vh"
      append-to-body
      :close-on-click-modal="false"
      @closed="handleDetailClosed"
    >
      <div class="detail-toolbar">
        <div class="detail-merchant-info">
          <span>商户电话：{{ detailMerchant && detailMerchant.merchantPhone ? detailMerchant.merchantPhone : '-' }}</span>
          <span>共 {{ detailRows.length }} 项价格明细</span>
        </div>
        <el-button
          type="danger"
          size="small"
          icon="el-icon-delete"
          plain
          :disabled="!detailSelectedRows.length"
          @click="handleDetailBatchDelete"
        >批量删除{{ detailSelectedRows.length ? `（${detailSelectedRows.length}）` : '' }}</el-button>
      </div>
      <el-table
        v-loading="detailLoading"
        :data="detailRows"
        row-key="id"
        border
        stripe
        size="small"
        max-height="560"
        @selection-change="handleDetailSelectionChange"
      >
        <el-table-column type="selection" width="45" align="center" />
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column v-if="isCeiling" label="产品型号" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.goodsFilterName || '-' }}</template>
        </el-table-column>
        <el-table-column label="计价项" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.pricingItemName || '基础面积' }}</template>
        </el-table-column>
        <el-table-column label="商户单价" min-width="110" align="right">
          <template slot-scope="scope">
            <span v-if="hasMerchantPrice(scope.row.price)">{{ scope.row.price }}</span>
            <span v-else class="empty-text">使用标准价</span>
          </template>
        </el-table-column>
        <el-table-column v-if="isCeiling" label="保底补贴单价" min-width="130" align="right">
          <template slot-scope="scope">
            <template v-if="isCeilingPanelFilter(scope.row.goodsFilterId)">
              <span v-if="hasMerchantPrice(scope.row.subsidyPrice)">{{ scope.row.subsidyPrice }}</span>
              <span v-else class="empty-text">使用型号标准价</span>
            </template>
            <span v-else class="empty-text">-</span>
          </template>
        </el-table-column>
        <el-table-column label="当前生效价" min-width="160" align="right">
          <template slot-scope="scope">
            <div v-if="effectivePriceSource(scope.row) === '调价任务'" class="adjust-info">
              <span class="adjust-price">{{ formatPrice(effectivePrice(scope.row)) }}</span>
              <span class="adjust-tip">调价任务</span>
            </div>
            <div v-else-if="effectivePrice(scope.row) !== null" class="effective-price-info">
              <span class="price-text">{{ formatPrice(effectivePrice(scope.row)) }}</span>
              <span class="effective-price-source">{{ effectivePriceSource(scope.row) }}</span>
            </div>
            <span v-else class="empty-text">未配置（按0计）</span>
          </template>
        </el-table-column>
        <el-table-column label="计价方式" width="150" align="center">
          <template slot-scope="scope">
            <el-tag
              size="small"
              :type="effectivePriceSource(scope.row) === '调价任务' ? 'danger' : (hasMerchantPrice(scope.row.price) ? 'warning' : 'success')"
            >{{ effectivePriceSource(scope.row) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-switch
              :value="scope.row.status === 'ENABLE'"
              :disabled="scope.row.pendingApproval || scope.row.pendingAction === 'DELETE'"
              @change="handleToggleStatus(scope.row, $event)"
            />
          </template>
        </el-table-column>
        <el-table-column label="审批状态" width="150" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.pendingAction" type="warning" size="small">
              待提交{{ pendingActionText(scope.row.pendingAction) }}
            </el-tag>
            <el-tag v-else-if="scope.row.pendingApproval" type="primary" size="small">
              审批中{{ scope.row.pendingApprovalAction ? `：${scope.row.pendingApprovalAction}` : '' }}
            </el-tag>
            <span v-else class="empty-text">-</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="text"
              size="small"
              icon="el-icon-delete"
              style="color: #f56c6c"
              :disabled="scope.row.pendingApproval || scope.row.pendingAction === 'DELETE'"
              @click="handleDelete(scope.row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 新增商户价弹窗 -->
    <el-dialog
      :title="editingPendingIndex == null ? '批量新增商户价' : '修改暂存明细'"
      :visible.sync="addDialogVisible"
      width="720px"
      append-to-body
      :close-on-click-modal="false"
      @closed="handleAddClosed"
    >
      <el-form ref="addForm" :model="addForm" :rules="addRules" label-width="110px">
        <el-form-item label="商户" prop="merchantIds">
          <el-select
            v-model="addForm.merchantIds"
            filterable
            remote
            multiple
            collapse-tags
            :remote-method="searchMerchant"
            :loading="merchantLoading"
            placeholder="输入商户名称搜索，可多选"
            style="width: 100%"
            @change="handleMerchantIdsChange"
          >
            <el-option
              v-for="item in merchantOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          v-if="isCeiling && !addForm.usePriceBookPrice"
          label="产品型号"
          prop="goodsFilterIds"
        >
          <div class="goods-filter-select-line">
            <el-select
              v-model="addForm.goodsFilterIds"
              filterable
              multiple
              collapse-tags
              placeholder="请选择扣板或电器型号"
              style="flex: 1"
              @change="handleGoodsFilterIdsChange"
            >
              <el-option-group
                v-for="group in goodsFilterGroups"
                :key="group.label"
                :label="group.label"
              >
                <el-option
                  v-for="item in group.options"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-option-group>
            </el-select>
            <el-button
              type="text"
              size="small"
              :disabled="!allGoodsFilterIds.length"
              @click="toggleAllGoodsFilters"
            >{{ allGoodsFiltersSelected ? "取消全选" : "全选" }}</el-button>
          </div>
        </el-form-item>
        <el-form-item
          v-if="!isCeiling && !addForm.usePriceBookPrice"
          label="计价项"
          prop="pricingItemCodes"
        >
          <div class="pricing-item-select-line">
            <el-select
              v-model="addForm.pricingItemCodes"
              multiple
              collapse-tags
              filterable
              placeholder="请选择一个或多个计价项"
              style="flex: 1"
              @change="handlePricingItemCodesChange"
            >
              <el-option
                v-for="item in pricingItems"
                :key="item.pricingItemCode"
                :label="item.pricingItemName || item.pricingItemCode"
                :value="item.pricingItemCode"
              />
            </el-select>
            <el-button
              type="text"
              size="small"
              :disabled="!allPricingItemCodes.length"
              @click="toggleAllPricingItems"
            >{{ allPricingItemsSelected ? "取消全选" : "全选" }}</el-button>
          </div>
        </el-form-item>
        <el-form-item label="价格设置">
          <el-radio-group v-model="addForm.usePriceBookPrice">
            <el-radio :label="true">使用价目表价格</el-radio>
            <el-radio :label="false">单独设置商户价格</el-radio>
          </el-radio-group>
          <div class="form-tip">
            使用价目表价格时，将自动应用{{ isCeiling ? '全部产品型号' : '价目表全部计价项' }}，后续修改标准价会自动生效
          </div>
        </el-form-item>
        <el-form-item
          v-if="!addForm.usePriceBookPrice"
          :label="isCeiling ? '型号价格' : '商户单价'"
        >
          <div v-if="!isCeiling && merchantPricingItemRows.length" class="pricing-price-list merchant-pricing-item-list">
            <div v-for="group in merchantPricingItemGroups" :key="group.key" class="merchant-filter-price-group">
              <div class="merchant-filter-price-group-title-row">
                <div class="merchant-filter-price-group-title" :title="group.merchantName">
                  {{ group.merchantName }}<span v-if="group.merchantPhone">（{{ group.merchantPhone }}）</span>
                </div>
                <el-button
                  type="text"
                  size="small"
                  class="merchant-filter-group-toggle"
                  :aria-expanded="isMerchantPricingItemGroupExpanded(group.key)"
                  @click="toggleMerchantPricingItemGroup(group.key)"
                >
                  <i :class="isMerchantPricingItemGroupExpanded(group.key) ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
                  {{ isMerchantPricingItemGroupExpanded(group.key) ? '收起' : '展开' }}
                </el-button>
              </div>
              <div v-if="isMerchantPricingItemGroupExpanded(group.key)">
                <div class="merchant-filter-price-header merchant-pricing-item-header">
                  <span>计价项</span>
                  <span>商户单价</span>
                  <span class="pricing-price-tip-placeholder" />
                </div>
                <div v-for="row in group.rows" :key="row.key" class="pricing-price-row merchant-pricing-item-row">
                  <span class="pricing-price-name merchant-filter-price-name" :title="row.pricingItemName">
                    {{ row.pricingItemName }}
                  </span>
                  <el-input-number
                    v-model="addForm.merchantPricingItemPrices[row.key]"
                    :min="0"
                    :precision="4"
                    :controls="false"
                    placeholder="0表示使用标准价"
                    class="pricing-price-input"
                  />
                  <span class="pricing-price-tip">0或留空使用标准价</span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="isCeiling && merchantFilterPriceRows.length" class="pricing-price-list merchant-filter-price-list">
            <div v-for="group in merchantFilterPriceGroups" :key="group.key" class="merchant-filter-price-group">
              <div class="merchant-filter-price-group-title-row">
                <div class="merchant-filter-price-group-title" :title="group.merchantName">
                  {{ group.merchantName }}<span v-if="group.merchantPhone">（{{ group.merchantPhone }}）</span>
                </div>
                <el-button
                  type="text"
                  size="small"
                  class="merchant-filter-group-toggle"
                  :aria-expanded="isMerchantFilterGroupExpanded(group.key)"
                  @click="toggleMerchantFilterGroup(group.key)"
                >
                  <i :class="isMerchantFilterGroupExpanded(group.key) ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
                  {{ isMerchantFilterGroupExpanded(group.key) ? '收起' : '展开' }}
                </el-button>
              </div>
              <div v-if="isMerchantFilterGroupExpanded(group.key)">
                <div class="merchant-filter-price-header merchant-filter-price-header--double">
                  <span>产品型号</span>
                  <span>商户单价</span>
                  <span>保底补贴单价</span>
                </div>
                <div v-for="row in group.rows" :key="row.key" class="pricing-price-row merchant-filter-price-row">
                  <span class="pricing-price-name merchant-filter-price-name" :title="row.filterName">{{ row.filterName }}</span>
                  <el-input-number
                    v-model="addForm.merchantFilterPrices[row.key]"
                    :min="0"
                    :precision="4"
                    :controls="false"
                    placeholder="型号标准价"
                    class="pricing-price-input"
                  />
                  <el-input-number
                    v-if="String(row.calculateType) === '2'"
                    v-model="addForm.merchantFilterSubsidyPrices[row.key]"
                    :min="0"
                    :precision="2"
                    :controls="false"
                    placeholder="型号标准价"
                    class="pricing-price-input"
                  />
                  <span v-else class="empty-text">不适用</span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="!isCeiling && !addForm.pricingItemCodes.length" class="form-tip">
            请先选择计价项，再分别填写每个计价项的商户单价
          </div>
          <div v-if="!isCeiling" class="form-tip">单价为 0 或留空表示该计价项未配置，使用价盘标准价</div>
          <div v-if="isCeiling" class="form-tip">商户单价默认取型号标准价；扣板可另设保底补贴单价，电器不参与面积保底</div>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="addSubmitting" @click="handleAddSubmit">
          {{ editingPendingIndex == null ? '暂存批量新增' : '保存修改' }}
        </el-button>
      </span>
    </el-dialog>

    <!-- 批量导入弹窗 -->
    <el-dialog
      title="批量导入商户价"
      :visible.sync="importDialogVisible"
      width="80%"
      append-to-body
      :close-on-click-modal="false"
      @closed="handleImportClosed"
    >
      <div class="import-steps">
        <div class="import-step">
          <span class="step-num">1</span>
          <el-button type="text" icon="el-icon-download" :loading="templateLoading" @click="handleDownloadTemplate">
            下载导入模板
          </el-button>
          <span class="step-tip">模板含填写示例与商户/型号清单，只填名称即可，「示例：」开头的行会自动跳过</span>
        </div>
        <div class="import-step">
          <span class="step-num">2</span>
          <el-radio-group v-model="importMode" size="small">
            <el-radio-button label="INSERT">新增模式</el-radio-button>
            <el-radio-button label="OVERWRITE">覆盖模式</el-radio-button>
          </el-radio-group>
          <span class="step-tip">{{ importMode === 'INSERT' ? '已配置过的商户价会报错' : '已配置过的商户价将更新价格' }}</span>
        </div>
        <div class="import-step">
          <span class="step-num">3</span>
          <el-upload
            ref="importUpload"
            action="#"
            :auto-upload="false"
            :limit="1"
            accept=".xlsx,.xls"
            :file-list="importFileList"
            :on-change="handleImportFileChange"
            :on-remove="handleImportFileRemove"
            :on-exceed="handleImportFileExceed"
          >
            <el-button size="small" icon="el-icon-folder-opened">选择文件</el-button>
          </el-upload>
          <el-button
            type="primary"
            size="small"
            :disabled="!importFile"
            :loading="previewLoading"
            @click="handlePreview"
          >上传预览</el-button>
        </div>
      </div>

      <template v-if="previewResult">
        <el-alert
          :title="`共 ${previewResult.totalRows} 行，有效 ${previewResult.validRows} 行，无效 ${previewResult.invalidRows} 行${previewResult.invalidRows > 0 ? '（红色行不会导入，请修正后重新上传）' : ''}`"
          :type="previewResult.invalidRows > 0 ? 'warning' : 'success'"
          :closable="false"
          show-icon
          style="margin-bottom: 12px"
        />
        <el-table
          :data="previewResult.rows"
          border
          stripe
          size="small"
          max-height="360"
          :row-class-name="previewRowClass"
        >
          <el-table-column prop="rowNo" label="行号" width="70" align="center" />
          <el-table-column prop="merchantName" label="商户名称" min-width="140" show-overflow-tooltip />
          <el-table-column prop="merchantPhone" label="商户电话" min-width="110">
            <template slot-scope="scope">{{ scope.row.merchantPhone || '-' }}</template>
          </el-table-column>
          <el-table-column v-if="isCeiling" prop="goodsFilterName" label="型号名" min-width="120">
            <template slot-scope="scope">{{ scope.row.goodsFilterName || '-' }}</template>
          </el-table-column>
          <el-table-column v-if="!isCeiling" prop="pricingItemName" label="计价项" min-width="120">
            <template slot-scope="scope">{{ scope.row.pricingItemName || '基础面积' }}</template>
          </el-table-column>
          <el-table-column prop="price" label="商户单价" min-width="100" align="right">
            <template slot-scope="scope">{{ scope.row.price == null || scope.row.price === '' ? '使用标准价' : scope.row.price }}</template>
          </el-table-column>
          <el-table-column v-if="isCeiling" prop="subsidyPrice" label="保底补贴单价" min-width="120" align="right">
            <template slot-scope="scope">
              {{ String(scope.row.goodsCalculateType) === '2'
                ? (scope.row.subsidyPrice == null || scope.row.subsidyPrice === '' ? '使用型号标准价' : scope.row.subsidyPrice)
                : '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="action" label="操作" width="80" align="center" />
          <el-table-column label="校验结果" min-width="160">
            <template slot-scope="scope">
              <span v-if="scope.row.error" class="error-text">{{ scope.row.error }}</span>
              <span v-else style="color: #67c23a">通过</span>
            </template>
          </el-table-column>
        </el-table>
      </template>

      <span slot="footer" class="dialog-footer">
        <el-button @click="importDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          :disabled="!validPreviewRows.length"
          :loading="confirmLoading"
          @click="handleConfirmImport"
        >确认导入{{ validPreviewRows.length ? `（${validPreviewRows.length} 行）` : '' }}</el-button>
      </span>
    </el-dialog>
  </el-dialog>
</template>

<script>
import {
  findPriceBookMerchantSummary,
  findPriceBookMerchantDetails,
  submitPriceBookMerchantBatch,
  downloadMerchantImportTemplate,
  previewMerchantImport,
  confirmMerchantImport,
  getPriceBookDetail,
  findPriceBookFilterPrice,
} from "@/api/work/waterProof/merchantPrice";
import { findShopListV2 } from "@/api/salesCloud/shop";
import { findPureWaterGoodsByRootDepartId } from "@/api/salesCloud/goodsQr";
import { pricingItemDic } from "@/const/work/waterProof/merchantPriceOption";

export default {
  name: "MerchantPriceDrawer",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    priceBook: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      dialogVisible: false,
      tableLoading: false,
      tableData: [],
      searchName: "",
      selectedRows: [],
      batchDeleteLoading: false,
      page: { total: 0, currentPage: 1, pageSize: 10 },
      detailDialogVisible: false,
      detailLoading: false,
      detailMerchant: null,
      detailRows: [],
      detailSelectedRows: [],
      // 新增
      addDialogVisible: false,
      addSubmitting: false,
      editingPendingIndex: null,
      addForm: {
        merchantIds: [],
        goodsFilterIds: [],
        usePriceBookPrice: true,
        merchantFilterPrices: {},
        merchantFilterSubsidyPrices: {},
        merchantPricingItemPrices: {},
        pricingItemCodes: [],
        pricingItemPrices: {},
      },
      addRules: {
        merchantIds: [{ required: true, message: "请选择至少一个商户", trigger: "change" }],
        goodsFilterIds: [{ required: true, message: "请选择至少一个产品型号", trigger: "change" }],
        pricingItemCodes: [{ required: true, message: "请选择计价项", trigger: "change" }],
      },
      merchantOptions: [],
      merchantLoading: false,
      goodsFilterGroups: [],
      expandedMerchantFilterGroups: {},
      expandedMerchantPricingItemGroups: {},
      pricingItems: [],
      // 商户价调整暂存队列：按价盘保存在浏览器，最后统一提交审批
      pendingChanges: [],
      pendingDialogVisible: false,
      pendingSubmitting: false,
      pendingSelectedRows: [],
      pendingPage: { currentPage: 1, pageSize: 20 },
      // 批量导入
      templateLoading: false,
      importDialogVisible: false,
      importMode: "INSERT",
      importFile: null,
      importFileList: [],
      previewLoading: false,
      previewResult: null,
      confirmLoading: false,
    };
  },
  computed: {
    // 预览中校验通过的行（确认导入时原样回传）
    validPreviewRows() {
      if (!this.previewResult || !this.previewResult.rows) return [];
      return this.previewResult.rows.filter((r) => !r.error);
    },
    isCeiling() {
      return this.priceBook && this.priceBook.projectType === "HANG_CEILING";
    },
    projectTypeText() {
      const map = { WATERPROOF: "防水", BACKFILL: "回填", HANG_CEILING: "吊顶" };
      return this.priceBook ? map[this.priceBook.projectType] || this.priceBook.projectType : "";
    },
    pendingChangeCount() {
      return this.pendingChanges.length;
    },
    pendingPageRows() {
      const start = (this.pendingPage.currentPage - 1) * this.pendingPage.pageSize;
      return this.pendingChanges
        .slice(start, start + this.pendingPage.pageSize)
        .map((row, index) => Object.assign({ _pendingIndex: start + index }, row));
    },
    merchantFilterPriceRows() {
      const merchantIds = Array.isArray(this.addForm.merchantIds)
        ? this.addForm.merchantIds : [];
      const filterIds = Array.isArray(this.addForm.goodsFilterIds)
        ? this.addForm.goodsFilterIds : [];
      return merchantIds.reduce((rows, merchantId) => {
        const merchant = this.merchantOptions.find((item) => item.value === merchantId) || {};
        filterIds.forEach((goodsFilterId) => {
          const filter = this.getGoodsFilterOption(goodsFilterId) || {};
          rows.push({
            key: this.merchantFilterKey(merchantId, goodsFilterId),
            merchantId,
            merchantName: merchant.merchantName || merchant.label || merchantId,
            merchantPhone: merchant.merchantPhone || "",
            goodsFilterId,
            filterName: filter.label || goodsFilterId,
            calculateType: filter.calculateType,
          });
        });
        return rows;
      }, []);
    },
    merchantFilterPriceGroups() {
      const groups = [];
      const groupMap = {};
      this.merchantFilterPriceRows.forEach((row) => {
        let group = groupMap[row.merchantId];
        if (!group) {
          group = {
            key: row.merchantId,
            merchantName: row.merchantName,
            merchantPhone: row.merchantPhone,
            rows: [],
          };
          groupMap[row.merchantId] = group;
          groups.push(group);
        }
        group.rows.push(row);
      });
      return groups;
    },
    merchantPricingItemRows() {
      const merchantIds = Array.isArray(this.addForm.merchantIds)
        ? this.addForm.merchantIds : [];
      const pricingItemCodes = Array.isArray(this.addForm.pricingItemCodes)
        ? this.addForm.pricingItemCodes : [];
      return merchantIds.reduce((rows, merchantId) => {
        const merchant = this.merchantOptions.find((item) => item.value === merchantId) || {};
        pricingItemCodes.forEach((pricingItemCode) => {
          rows.push({
            key: this.merchantPricingItemKey(merchantId, pricingItemCode),
            merchantId,
            merchantName: merchant.merchantName || merchant.label || merchantId,
            merchantPhone: merchant.merchantPhone || "",
            pricingItemCode,
            pricingItemName: this.getPricingItemName(pricingItemCode),
          });
        });
        return rows;
      }, []);
    },
    merchantPricingItemGroups() {
      const groups = [];
      const groupMap = {};
      this.merchantPricingItemRows.forEach((row) => {
        let group = groupMap[row.merchantId];
        if (!group) {
          group = {
            key: row.merchantId,
            merchantName: row.merchantName,
            merchantPhone: row.merchantPhone,
            rows: [],
          };
          groupMap[row.merchantId] = group;
          groups.push(group);
        }
        group.rows.push(row);
      });
      return groups;
    },
    allPricingItemCodes() {
      return (this.pricingItems || [])
        .map((item) => item && item.pricingItemCode)
        .filter((code) => !!code);
    },
    configuredPricingItemCodes() {
      return (this.pricingItems || [])
        .filter((item) => item && item._configuredInPriceBook)
        .map((item) => item.pricingItemCode)
        .filter((code) => !!code);
    },
    allPricingItemsSelected() {
      const selectedCodes = Array.isArray(this.addForm.pricingItemCodes)
        ? this.addForm.pricingItemCodes : [];
      return this.allPricingItemCodes.length > 0
        && this.allPricingItemCodes.every((code) => selectedCodes.includes(code));
    },
    allGoodsFilterIds() {
      return this.goodsFilterGroups.reduce(
        (result, group) => result.concat((group.options || []).map((item) => item.value)),
        []
      );
    },
    allGoodsFiltersSelected() {
      const selectedIds = Array.isArray(this.addForm.goodsFilterIds)
        ? this.addForm.goodsFilterIds : [];
      return this.allGoodsFilterIds.length > 0
        && this.allGoodsFilterIds.every((id) => selectedIds.includes(id));
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.dialogVisible = val;
        if (val && this.priceBook) {
          this.searchName = "";
          this.selectedRows = [];
          this.pendingSelectedRows = [];
          this.pendingPage.currentPage = 1;
          this.loadPendingChanges();
          this.page.currentPage = 1;
          this.handleList();
          this.loadPricingItems();
          if (this.isCeiling) this.loadGoodsFilters();
        }
      },
    },
    dialogVisible(val) {
      this.$emit("update:visible", val);
    },
  },
  methods: {
    parseData(res) {
      if (!res || res.data == null) return {};
      if (typeof res.data === "string") {
        try {
          return JSON.parse(res.data) || {};
        } catch (e) {
          console.error("parseData error:", e);
          return {};
        }
      }
      return res.data;
    },
    pendingStorageKey() {
      return this.priceBook && this.priceBook.id
        ? `joins-sales-cloud:price-book-merchant-pending:${this.priceBook.id}`
        : "";
    },
    loadPendingChanges() {
      this.pendingChanges = [];
      const key = this.pendingStorageKey();
      if (!key) return;
      try {
        const value = window.localStorage.getItem(key);
        const parsed = value ? JSON.parse(value) : [];
        this.pendingChanges = Array.isArray(parsed) ? parsed : [];
        this.sortPendingChanges();
        this.normalizePendingPage();
      } catch (e) {
        console.warn("loadPendingChanges error:", e);
        this.pendingChanges = [];
      }
    },
    persistPendingChanges() {
      const key = this.pendingStorageKey();
      if (!key) return;
      try {
        if (this.pendingChanges.length) {
          window.localStorage.setItem(key, JSON.stringify(this.pendingChanges));
        } else {
          window.localStorage.removeItem(key);
        }
      } catch (e) {
        // 暂存失败不影响当前页面继续操作，提交时仍会使用内存队列。
        console.warn("persistPendingChanges error:", e);
      }
    },
    pendingChangeKey(change) {
      if (!change) return "";
      if (change.action === "ADD") {
        return `ADD|${change.merchantId || ""}|${change.goodsFilterId || ""}|${change.pricingItemCode || ""}`;
      }
      return `RECORD|${change.merchantPriceId || ""}`;
    },
    queuePendingChange(change) {
      this.queuePendingChanges([change]);
    },
    queuePendingChanges(changes) {
      const indexByKey = {};
      this.pendingChanges.forEach((item, index) => {
        indexByKey[this.pendingChangeKey(item)] = index;
      });
      (changes || []).forEach((change) => {
        const next = Object.assign({}, change);
        const key = this.pendingChangeKey(next);
        const index = Object.prototype.hasOwnProperty.call(indexByKey, key)
          ? indexByKey[key] : -1;
        if (index < 0) {
          indexByKey[key] = this.pendingChanges.length;
          this.pendingChanges.push(next);
        } else if (next.action === "DELETE") {
          this.$set(this.pendingChanges, index, next);
        } else {
          // 同一条记录连续改价、启停时合并为最后状态，保留首次快照信息。
          this.$set(this.pendingChanges, index, Object.assign({}, this.pendingChanges[index], next));
        }
      });
      this.sortPendingChanges();
      this.normalizePendingPage();
      this.persistPendingChanges();
    },
    sortPendingChanges() {
      const text = (value) => String(value == null ? "" : value).trim();
      const compare = (left, right) => {
        const fields = [
          "merchantName",
          "merchantPhone",
          "merchantId",
          "goodsFilterName",
          "pricingItemName",
          "pricingItemCode",
          "action",
        ];
        for (let i = 0; i < fields.length; i += 1) {
          const result = text(left && left[fields[i]]).localeCompare(
            text(right && right[fields[i]]),
            "zh-CN"
          );
          if (result !== 0) return result;
        }
        return 0;
      };
      this.pendingChanges = this.pendingChanges.slice().sort(compare);
    },
    normalizePendingPage() {
      const maxPage = Math.max(1, Math.ceil(this.pendingChanges.length / this.pendingPage.pageSize));
      if (this.pendingPage.currentPage > maxPage) this.pendingPage.currentPage = maxPage;
    },
    handlePendingPageChange(page) {
      this.pendingPage.currentPage = page;
      this.pendingSelectedRows = [];
      this.$nextTick(() => {
        if (this.$refs.pendingTable) this.$refs.pendingTable.clearSelection();
      });
    },
    pendingActionText(action) {
      return { ADD: "新增", UPDATE: "修改/启停", DELETE: "删除" }[action] || action || "-";
    },
    pendingStatusText(status) {
      return { ENABLE: "启用", DISABLE: "停用", DRAFT: "草稿" }[status] || status || "-";
    },
    openPendingDialog() {
      if (!this.pendingChangeCount) {
        this.$message.info("暂无待提交的调价");
        return;
      }
      this.normalizePendingPage();
      this.pendingDialogVisible = true;
    },
    removePendingChange(index) {
      this.pendingChanges.splice(index, 1);
      this.pendingSelectedRows = [];
      if (this.$refs.pendingTable) this.$refs.pendingTable.clearSelection();
      this.persistPendingChanges();
      this.normalizePendingPage();
      if (!this.pendingChanges.length) this.pendingDialogVisible = false;
      this.handleList();
    },
    handlePendingSelectionChange(rows) {
      this.pendingSelectedRows = rows || [];
    },
    removeSelectedPendingChanges() {
      const indexes = this.pendingSelectedRows
        .map((row) => Number(row && row._pendingIndex))
        .filter((index) => Number.isInteger(index) && index >= 0 && index < this.pendingChanges.length)
        .filter((index, position, all) => all.indexOf(index) === position)
        .sort((left, right) => right - left);
      if (!indexes.length) return;
      this.$confirm(`确定移除选中的 ${indexes.length} 条暂存明细吗？`, "提示", {
        confirmButtonText: "确定移除",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          indexes.forEach((index) => this.pendingChanges.splice(index, 1));
          this.pendingSelectedRows = [];
          if (this.$refs.pendingTable) this.$refs.pendingTable.clearSelection();
          this.persistPendingChanges();
          this.normalizePendingPage();
          if (!this.pendingChanges.length) this.pendingDialogVisible = false;
          this.$message.success(`已移除 ${indexes.length} 条暂存明细`);
          this.handleList();
        })
        .catch(() => {});
    },
    editPendingChange(index) {
      const pending = this.pendingChanges[index];
      if (!pending || pending.action !== "ADD") return;
      this.editingPendingIndex = index;
      this.addForm = {
        merchantIds: pending.merchantId ? [pending.merchantId] : [],
        goodsFilterIds: pending.goodsFilterId ? [pending.goodsFilterId] : [],
        usePriceBookPrice:
          !this.hasMerchantPrice(pending.price) && !this.hasMerchantPrice(pending.subsidyPrice),
        merchantFilterPrices: {
          [this.merchantFilterKey(pending.merchantId || "", pending.goodsFilterId || "")]:
            pending.price == null ? 0 : pending.price,
        },
        merchantFilterSubsidyPrices: {
          [this.merchantFilterKey(pending.merchantId || "", pending.goodsFilterId || "")]:
            pending.subsidyPrice == null ? 0 : pending.subsidyPrice,
        },
        merchantPricingItemPrices: !this.isCeiling && pending.pricingItemCode
          ? {
            [this.merchantPricingItemKey(pending.merchantId || "", pending.pricingItemCode)]:
              pending.price == null ? 0 : pending.price,
          }
          : {},
        pricingItemCodes: pending.pricingItemCode ? [pending.pricingItemCode] : [],
        pricingItemPrices: {},
      };
      // 远程搜索结果可能已被替换，先补回当前明细的商户选项，保证编辑时能显示名称。
      if (pending.merchantId && !this.merchantOptions.some((item) => item.value === pending.merchantId)) {
        this.merchantOptions.push({
          value: pending.merchantId,
          label: pending.merchantPhone
            ? `${pending.merchantName || pending.merchantId}（${pending.merchantPhone}）`
            : (pending.merchantName || pending.merchantId),
          merchantName: pending.merchantName || "",
          merchantPhone: pending.merchantPhone || "",
        });
      }
      this.loadPricingItems();
      this.addDialogVisible = true;
    },
    submitPendingChanges() {
      if (!this.priceBook || !this.pendingChanges.length) return;
      this.pendingSubmitting = true;
      submitPriceBookMerchantBatch({
        priceBookId: this.priceBook.id,
        items: this.pendingChanges,
      })
        .then((res) => {
          if (res.code === "200") {
            const data = this.parseData(res) || {};
            this.pendingChanges = [];
            this.pendingSelectedRows = [];
            this.persistPendingChanges();
            this.pendingDialogVisible = false;
            this.$message.success(data.message || "已提交审批，全部通过后生效");
            this.selectedRows = [];
            this.handleList();
            if (this.detailDialogVisible) this.loadMerchantDetails();
          } else {
            this.$message.error(res.message || "提交审批失败");
          }
        })
        .catch((err) => {
          console.log("submitPendingChanges error:", err);
          this.$message.error("提交审批异常");
        })
        .finally(() => {
          this.pendingSubmitting = false;
        });
    },
    applyPendingToRows(rows) {
      const pendingById = {};
      this.pendingChanges.forEach((item) => {
        if (item.merchantPriceId) pendingById[item.merchantPriceId] = item;
      });
      return (rows || []).map((row) => {
        // 保留接口返回的真实状态。暂存状态会覆盖 row.status，但撤销启停时仍需和真实状态比较。
        if (!Object.prototype.hasOwnProperty.call(row, "_baseStatus")) {
          this.$set(row, "_baseStatus", row.status);
        }
        const pending = pendingById[row.id];
        if (!pending) return row;
        if (pending.action === "UPDATE") {
          if (Object.prototype.hasOwnProperty.call(pending, "price")) this.$set(row, "price", pending.price);
          if (Object.prototype.hasOwnProperty.call(pending, "subsidyPrice")) this.$set(row, "subsidyPrice", pending.subsidyPrice);
          if (pending.status) this.$set(row, "status", pending.status);
        }
        this.$set(row, "pendingAction", pending.action);
        return row;
      });
    },
    // 调价失效时间格式化（毫秒时间戳 → yyyy-MM-dd）
    formatAdjustEnd(v) {
      if (v == null || v === "") return "-";
      if (typeof v === "string" && !/^\d+$/.test(v)) return v;
      const d = new Date(Number(v));
      if (isNaN(d.getTime())) return "-";
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    // 商户汇总列表（接口 page 从 0 开始）
    handleList() {
      if (!this.priceBook) return;
      this.tableLoading = true;
      findPriceBookMerchantSummary({
        priceBookId: this.priceBook.id,
        merchantName: this.searchName || undefined,
        page: this.page.currentPage - 1,
        size: this.page.pageSize,
      })
        .then((res) => {
          if (res.code === "200") {
            const data = this.parseData(res);
            this.tableData = data.rows || [];
            this.page.total = data.total || 0;
          } else {
            this.tableData = [];
            this.page.total = 0;
            this.$message.error(res.message || "加载失败");
          }
        })
        .catch((err) => {
          console.error("MerchantPriceDrawer handleList error:", err);
          this.tableData = [];
          this.page.total = 0;
          this.$message.error("加载异常");
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },
    handleSearch() {
      this.page.currentPage = 1;
      this.handleList();
    },
    handleReset() {
      this.searchName = "";
      this.page.currentPage = 1;
      this.handleList();
    },
    handlePageChange(page) {
      this.page.currentPage = page;
      this.handleList();
    },
    handleSizeChange(size) {
      this.page.pageSize = size;
      this.page.currentPage = 1;
      this.handleList();
    },
    handleSelectionChange(selection) {
      this.selectedRows = selection || [];
    },
    summaryPendingChangeCount(row) {
      if (!row) return 0;
      return this.pendingChanges.filter((item) => {
        if (item.merchantId) return item.merchantId === row.merchantId;
        return item.merchantName === row.merchantName
          && (!item.merchantPhone || item.merchantPhone === row.merchantPhone);
      }).length;
    },
    openMerchantDetails(row) {
      this.detailMerchant = row;
      this.detailDialogVisible = true;
      this.loadMerchantDetails();
    },
    loadMerchantDetails() {
      if (!this.priceBook || !this.detailMerchant || !this.detailMerchant.merchantId) return;
      this.detailLoading = true;
      findPriceBookMerchantDetails({
        priceBookId: this.priceBook.id,
        merchantIds: this.detailMerchant.merchantId,
      })
        .then((res) => {
          if (res.code === "200") {
            const data = this.parseData(res);
            this.detailRows = this.applyPendingToRows(Array.isArray(data) ? data : []);
          } else {
            this.detailRows = [];
            this.$message.error(res.message || "加载商户价明细失败");
          }
        })
        .catch((err) => {
          console.error("MerchantPriceDrawer loadMerchantDetails error:", err);
          this.detailRows = [];
          this.$message.error("加载商户价明细异常");
        })
        .finally(() => {
          this.detailLoading = false;
        });
    },
    handleDetailSelectionChange(selection) {
      this.detailSelectedRows = selection || [];
    },
    handleDetailClosed() {
      this.detailMerchant = null;
      this.detailRows = [];
      this.detailSelectedRows = [];
    },
    // 吊顶：加载扣板、电器型号（calculateType=2/3），辅材不进入价盘
    loadGoodsFilters() {
      const goodsRequest = findPureWaterGoodsByRootDepartId("HANG_CEILING");
      const priceRequest = this.priceBook && this.priceBook.id
        ? findPriceBookFilterPrice(this.priceBook.id)
        : Promise.resolve(null);
      Promise.all([goodsRequest, priceRequest])
        .then(([goodsRes, priceRes]) => {
          const filterPrices = {};
          if (priceRes && priceRes.code === "200") {
            const data = this.parseData(priceRes) || [];
            (Array.isArray(data) ? data : []).forEach((item) => {
              if (item.goodsFilterId) filterPrices[item.goodsFilterId] = item;
            });
          }
          if (goodsRes.code === "200") {
            const data = this.parseData(goodsRes) || [];
            this.goodsFilterGroups = (Array.isArray(data) ? data : [])
              .filter((item) => ["2", "3"].includes(String(item.calculateType)))
              .map((item) => ({
                label: item.name,
                options: (item.filters || []).map((f) => {
                  const filterPrice = filterPrices[f.id];
                  return {
                    value: f.id,
                    label: f.value,
                    calculateType: item.calculateType,
                    standardPrice: filterPrice && filterPrice.price != null
                      ? Number(filterPrice.price)
                      : (f.customerPrice == null ? null : Number(f.customerPrice)),
                  };
                }),
              }))
              .filter((g) => g.options.length);
            if (this.addForm.goodsFilterIds.length) {
              this.handleGoodsFilterIdsChange(this.addForm.goodsFilterIds);
            }
          }
        })
        .catch((err) => {
          console.log("loadGoodsFilters error:", err);
        });
    },
    loadPricingItems() {
      if (!this.priceBook) return;
      const applyItems = (items) => {
        const configuredByCode = {};
        (Array.isArray(items) ? items : [])
          .filter((item) => item.status === "ENABLE" || item.status == null)
          .forEach((item) => {
            configuredByCode[String(item.pricingItemCode || "").toUpperCase()] = item;
          });
        const projectType = this.priceBook.projectType;
        const builtInItems = (pricingItemDic || [])
          .filter((item) => !item.projectTypes || item.projectTypes.includes(projectType))
          .map((item) => {
            const configured = configuredByCode[item.value];
            return configured
              ? Object.assign({}, configured, { _configuredInPriceBook: true })
              : {
                pricingItemCode: item.value,
                pricingItemName: item.label,
                standardPrice: null,
                _configuredInPriceBook: false,
              };
          });
        const builtInCodes = {};
        builtInItems.forEach((item) => {
          builtInCodes[String(item.pricingItemCode || "").toUpperCase()] = true;
        });
        // 保留价盘中已配置的自定义计价项，同时补齐该项目类型的内置项。
        const customItems = Object.keys(configuredByCode)
          .filter((code) => !builtInCodes[code])
          .map((code) => Object.assign(
            {},
            configuredByCode[code],
            { _configuredInPriceBook: true }
          ));
        this.pricingItems = builtInItems.concat(customItems);
      };
      if (Array.isArray(this.priceBook.pricingItems)) {
        applyItems(this.priceBook.pricingItems);
        return;
      }
      getPriceBookDetail(this.priceBook.id).then((res) => {
        if (res.code === "200") {
          const detail = this.parseData(res);
          applyItems(detail.pricingItems);
        }
      }).catch((err) => {
        console.log("loadPricingItems error:", err);
        if (!this.isCeiling) {
          this.pricingItems = (pricingItemDic || [])
            .filter((item) => !item.projectTypes || item.projectTypes.includes(this.priceBook.projectType))
            .map((item) => ({
              pricingItemCode: item.value,
              pricingItemName: item.label,
              standardPrice: null,
            }));
        }
      });
    },
    // 商户远程搜索（GET /shop/listV2）
    searchMerchant(keyword) {
      this.merchantLoading = true;
      const params = { page: 1, size: 20 };
      if (keyword) params.shopName = keyword;
      findShopListV2(params)
        .then((res) => {
          if (res.code === "200") {
            const data = this.parseData(res);
            const rows = Array.isArray(data) ? data : data.rows || [];
            const options = rows.map((r) => ({
              value: r.id,
              label: r.mobile ? `${r.shopName}（${r.mobile}）` : r.shopName,
              merchantName: r.shopName || r.name,
              merchantPhone: r.mobile,
            }));
            // 远程搜索会替换 options；保留已选商户，避免多选后再次搜索导致已选项显示为 ID。
            const selectedIds = Array.isArray(this.addForm.merchantIds) ? this.addForm.merchantIds : [];
            const selectedOptions = this.merchantOptions.filter((item) => selectedIds.includes(item.value));
            const byId = {};
            selectedOptions.concat(options).forEach((item) => {
              if (item && item.value) byId[item.value] = item;
            });
            this.merchantOptions = Object.keys(byId).map((id) => byId[id]);
          }
        })
        .catch((err) => {
          console.log("searchMerchant error:", err);
        })
        .finally(() => {
          this.merchantLoading = false;
        });
    },
    handleAdd() {
      this.editingPendingIndex = null;
      this.expandedMerchantFilterGroups = {};
      this.expandedMerchantPricingItemGroups = {};
      this.addForm = {
        merchantIds: [],
        goodsFilterIds: [],
        usePriceBookPrice: true,
        merchantFilterPrices: {},
        merchantFilterSubsidyPrices: {},
        merchantPricingItemPrices: {},
        pricingItemCodes: this.isCeiling ? ["BASE_AREA"] : [],
        pricingItemPrices: this.isCeiling ? { BASE_AREA: 0 } : {},
      };
      this.loadPricingItems();
      this.searchMerchant("");
      this.addDialogVisible = true;
    },
    handleAddSubmit() {
      this.$refs.addForm.validate((valid) => {
        if (!valid) return;
        const editingIndex = this.editingPendingIndex;
        const applyAllPriceBookPrices = this.addForm.usePriceBookPrice && editingIndex == null;
        const pricingItemCodes = this.isCeiling
          ? ["BASE_AREA"]
          : (applyAllPriceBookPrices
            ? this.configuredPricingItemCodes.slice()
            : (Array.isArray(this.addForm.pricingItemCodes)
              ? this.addForm.pricingItemCodes
              : [this.addForm.pricingItemCodes]).filter((code) => !!code));
        if (!pricingItemCodes.length) {
          this.$message.warning(
            applyAllPriceBookPrices ? "当前价目表没有已配置的计价项" : "请选择至少一个计价项"
          );
          return;
        }
        const merchantIds = Array.isArray(this.addForm.merchantIds)
          ? this.addForm.merchantIds.filter((id) => !!id)
          : [];
        if (!merchantIds.length) {
          this.$message.warning("请选择至少一个商户");
          return;
        }
        const goodsFilterIds = this.isCeiling
          ? (applyAllPriceBookPrices
            ? this.allGoodsFilterIds.slice()
            : (Array.isArray(this.addForm.goodsFilterIds)
              ? this.addForm.goodsFilterIds.filter((id) => !!id) : []))
          : [];
        if (this.isCeiling && !goodsFilterIds.length) {
          this.$message.warning(
            applyAllPriceBookPrices ? "当前价目表没有可用的产品型号" : "请选择至少一个产品型号"
          );
          return;
        }
        // 先完成表单和商户校验，再替换旧暂存项，避免校验失败导致原明细丢失。
        if (editingIndex != null) {
          this.pendingChanges.splice(editingIndex, 1);
          this.persistPendingChanges();
          this.editingPendingIndex = null;
        }
        let queuedCount = 0;
        merchantIds.forEach((merchantId) => {
          const merchant = this.merchantOptions.find((item) => item.value === merchantId) || {};
          pricingItemCodes.forEach((pricingItemCode) => {
            const targetFilterIds = this.isCeiling ? goodsFilterIds : [null];
            targetFilterIds.forEach((goodsFilterId) => {
              const priceKey = this.isCeiling
                ? this.merchantFilterKey(merchantId, goodsFilterId)
                : this.merchantPricingItemKey(merchantId, pricingItemCode);
              const configuredPrice = this.isCeiling
                ? this.addForm.merchantFilterPrices[priceKey]
                : (this.addForm.merchantPricingItemPrices
                  && Object.prototype.hasOwnProperty.call(
                    this.addForm.merchantPricingItemPrices,
                    priceKey
                  )
                  ? this.addForm.merchantPricingItemPrices[priceKey]
                  // 兼容旧版弹窗打开后仍保留的计价项级价格数据。
                  : (this.addForm.pricingItemPrices
                    && this.addForm.pricingItemPrices[pricingItemCode]));
              const filter = this.getGoodsFilterOption(goodsFilterId) || {};
              const configuredSubsidyPrice = this.isCeiling && String(filter.calculateType) === "2"
                ? this.addForm.merchantFilterSubsidyPrices[priceKey]
                : null;
              const payload = {
                action: "ADD",
                priceBookId: this.priceBook.id,
                merchantId,
                merchantName: merchant.merchantName || merchant.label || "",
                merchantPhone: merchant.merchantPhone || "",
                pricingItemCode,
                pricingItemName: this.isCeiling ? "基础面积" : this.getPricingItemName(pricingItemCode),
                price: !this.addForm.usePriceBookPrice && this.hasMerchantPrice(configuredPrice)
                  ? Number(configuredPrice) : null,
                subsidyPrice: !this.addForm.usePriceBookPrice
                  && this.isCeiling && this.hasMerchantPrice(configuredSubsidyPrice)
                  ? Number(configuredSubsidyPrice) : null,
              };
              if (this.isCeiling) {
                payload.goodsFilterId = goodsFilterId;
                payload.goodsFilterName = filter.label || "";
              }
              this.queuePendingChange(payload);
              queuedCount += 1;
            });
          });
        });
        this.$message.success(
          editingIndex == null
            ? `已暂存 ${queuedCount} 条新增明细（${merchantIds.length} 个商户），提交审核后统一生效`
            : "暂存明细已更新，提交审核后统一生效"
        );
        this.addDialogVisible = false;
        this.addSubmitting = false;
      });
    },
    getGoodsFilterOption(goodsFilterId) {
      if (!goodsFilterId) return null;
      for (let i = 0; i < this.goodsFilterGroups.length; i += 1) {
        const option = (this.goodsFilterGroups[i].options || [])
          .find((item) => item.value === goodsFilterId);
        if (option) return option;
      }
      return null;
    },
    isCeilingPanelFilter(goodsFilterId) {
      const option = this.getGoodsFilterOption(goodsFilterId);
      return option ? String(option.calculateType) === "2" : false;
    },
    merchantFilterKey(merchantId, goodsFilterId) {
      return `${merchantId || ""}::${goodsFilterId || ""}`;
    },
    isMerchantFilterGroupExpanded(key) {
      return this.expandedMerchantFilterGroups[key] !== false;
    },
    toggleMerchantFilterGroup(key) {
      this.$set(
        this.expandedMerchantFilterGroups,
        key,
        !this.isMerchantFilterGroupExpanded(key)
      );
    },
    toggleAllGoodsFilters() {
      const ids = this.allGoodsFilterIds.slice();
      const nextIds = this.allGoodsFiltersSelected ? [] : ids;
      this.$set(this.addForm, "goodsFilterIds", nextIds);
      this.handleGoodsFilterIdsChange(nextIds);
    },
    toggleAllPricingItems() {
      const codes = this.allPricingItemCodes.slice();
      const nextCodes = this.allPricingItemsSelected ? [] : codes;
      this.$set(this.addForm, "pricingItemCodes", nextCodes);
      this.handlePricingItemCodesChange(nextCodes);
    },
    handleGoodsFilterIdsChange(ids) {
      const selectedIds = Array.isArray(ids) ? ids : [];
      this.syncMerchantFilterPrices(selectedIds, this.addForm.merchantIds);
    },
    handleMerchantIdsChange(ids) {
      const selectedIds = Array.isArray(ids) ? ids : [];
      if (this.isCeiling) {
        this.syncMerchantFilterPrices(this.addForm.goodsFilterIds, selectedIds);
      } else {
        this.syncMerchantPricingItemPrices(this.addForm.pricingItemCodes, selectedIds);
      }
    },
    syncMerchantFilterPrices(filterIds, merchantIds) {
      const selectedFilterIds = Array.isArray(filterIds) ? filterIds : [];
      const selectedMerchantIds = Array.isArray(merchantIds) ? merchantIds : [];
      const oldPrices = this.addForm.merchantFilterPrices || {};
      const oldSubsidyPrices = this.addForm.merchantFilterSubsidyPrices || {};
      const nextPrices = {};
      const nextSubsidyPrices = {};
      selectedMerchantIds.forEach((merchantId) => {
        selectedFilterIds.forEach((goodsFilterId) => {
          const option = this.getGoodsFilterOption(goodsFilterId) || {};
          const standardPrice = option.standardPrice == null ? 0 : option.standardPrice;
          const key = this.merchantFilterKey(merchantId, goodsFilterId);
          nextPrices[key] = Object.prototype.hasOwnProperty.call(oldPrices, key)
            ? oldPrices[key] : standardPrice;
          if (String(option.calculateType) === "2") {
            nextSubsidyPrices[key] = Object.prototype.hasOwnProperty.call(oldSubsidyPrices, key)
              ? oldSubsidyPrices[key] : standardPrice;
          }
        });
      });
      this.$set(this.addForm, "merchantFilterPrices", nextPrices);
      this.$set(this.addForm, "merchantFilterSubsidyPrices", nextSubsidyPrices);
    },
    handlePricingItemCodesChange(codes) {
      const selectedCodes = Array.isArray(codes) ? codes : [];
      this.syncMerchantPricingItemPrices(selectedCodes, this.addForm.merchantIds);
    },
    merchantPricingItemKey(merchantId, pricingItemCode) {
      return `${merchantId || ""}::${pricingItemCode || ""}`;
    },
    isMerchantPricingItemGroupExpanded(key) {
      return this.expandedMerchantPricingItemGroups[key] !== false;
    },
    toggleMerchantPricingItemGroup(key) {
      this.$set(
        this.expandedMerchantPricingItemGroups,
        key,
        !this.isMerchantPricingItemGroupExpanded(key)
      );
    },
    syncMerchantPricingItemPrices(pricingItemCodes, merchantIds) {
      const selectedCodes = Array.isArray(pricingItemCodes) ? pricingItemCodes : [];
      const selectedMerchantIds = Array.isArray(merchantIds) ? merchantIds : [];
      const oldPrices = this.addForm.merchantPricingItemPrices || {};
      const nextPrices = {};
      selectedMerchantIds.forEach((merchantId) => {
        selectedCodes.forEach((pricingItemCode) => {
          const key = this.merchantPricingItemKey(merchantId, pricingItemCode);
          nextPrices[key] = Object.prototype.hasOwnProperty.call(oldPrices, key)
            ? oldPrices[key]
            : 0;
        });
      });
      this.$set(this.addForm, "merchantPricingItemPrices", nextPrices);
    },
    hasMerchantPrice(value) {
      return value !== null && value !== undefined && value !== "" && Number(value) > 0;
    },
    activeAdjustTask(row) {
      let task = row && row.activeAdjustTask;
      // 兼容部分网关把 transient 对象序列化成 JSON 字符串的返回格式。
      if (typeof task === "string") {
        try {
          task = JSON.parse(task);
        } catch (e) {
          task = null;
        }
      }
      return task && task.adjustedPrice !== null && task.adjustedPrice !== undefined
        ? task
        : null;
    },
    standardPrice(row) {
      if (!row) return null;
      if (this.isCeiling && row.goodsFilterId) {
        const filter = this.getGoodsFilterOption(row.goodsFilterId);
        if (!filter || filter.standardPrice === null || filter.standardPrice === undefined
          || filter.standardPrice === "") return null;
        const filterPrice = Number(filter.standardPrice);
        return Number.isFinite(filterPrice) && filterPrice > 0 ? filterPrice : null;
      }
      let code = String(row.pricingItemCode || "BASE_AREA").trim().toUpperCase();
      // 兼容旧商户价记录曾使用的 SECOND_ROW 编码。
      if (code === "SECOND_ROW") code = "SECOND_DRAIN";
      const item = (this.pricingItems || []).find((pricingItem) =>
        String(pricingItem.pricingItemCode || "").trim().toUpperCase() === code
      );
      if (!item) {
        return null;
      }
      const configured = item.standardPrice !== null && item.standardPrice !== undefined
        && item.standardPrice !== "" ? item.standardPrice : item.price;
      if (configured === null || configured === undefined || configured === "") return null;
      const value = Number(configured);
      return Number.isFinite(value) && value > 0 ? value : null;
    },
    effectivePrice(row) {
      const task = this.activeAdjustTask(row);
      if (task) return task.adjustedPrice;
      if (this.hasMerchantPrice(row && row.price)) return row.price;
      return this.standardPrice(row);
    },
    effectivePriceSource(row) {
      if (this.activeAdjustTask(row)) return "调价任务";
      if (this.hasMerchantPrice(row && row.price)) return "商户单价";
      if (this.standardPrice(row) !== null) return "价盘标准价";
      return "未配置";
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === "") return "-";
      const number = Number(value);
      return Number.isFinite(number) ? number.toFixed(2) : value;
    },
    getPricingItemName(code) {
      const item = this.pricingItems.find((row) => row.pricingItemCode === code);
      return item ? (item.pricingItemName || item.pricingItemCode) : code;
    },
    handleAddClosed() {
      this.editingPendingIndex = null;
      this.$refs.addForm && this.$refs.addForm.clearValidate();
    },
    handleToggleStatus(row, val) {
      if (row.pendingAction === "DELETE") {
        this.$message.warning("该记录已暂存删除，请先移除删除操作");
        return;
      }
      const target = val ? "ENABLE" : "DISABLE";
      const pendingIndex = this.pendingChanges.findIndex(
        (item) => this.pendingChangeKey(item) === this.pendingChangeKey({ merchantPriceId: row.id, action: "UPDATE" })
      );
      const pending = pendingIndex >= 0 ? this.pendingChanges[pendingIndex] : null;
      // 记录第一次切换前的真实状态；切回该状态时撤销暂存，避免留下无效的启用操作。
      // 优先使用接口返回的真实状态，兼容旧版暂存记录缺少 oldStatus 的情况。
      const originalStatus = row && row._baseStatus
        ? row._baseStatus
        : (pending && pending.oldStatus ? pending.oldStatus : row.status);
      if (pending && pending.action === "UPDATE" && originalStatus && target === originalStatus) {
        this.pendingChanges.splice(pendingIndex, 1);
        this.persistPendingChanges();
        this.normalizePendingPage();
        this.$set(row, "status", originalStatus);
        this.$delete(row, "pendingAction");
        this.$message.success("已取消暂存状态变更");
        this.handleList();
        return;
      }
      this.queuePendingChange({
        action: "UPDATE",
        merchantPriceId: row.id,
        merchantId: row.merchantId,
        merchantName: row.merchantName,
        merchantPhone: row.merchantPhone,
        goodsFilterName: row.goodsFilterName,
        pricingItemCode: row.pricingItemCode,
        pricingItemName: row.pricingItemName,
        price: row.price,
        oldStatus: originalStatus,
        status: target,
      });
      this.$set(row, "status", target);
      this.$set(row, "pendingAction", "UPDATE");
      this.$message.success("已暂存，提交审核后生效");
    },
    handleDelete(row) {
      this.$confirm(`确定删除商户「${row.merchantName}」的价格配置吗？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => this.doDelete([row]))
        .catch(() => {});
    },
    handleBatchDelete() {
      if (!this.selectedRows.length) {
        this.$message.warning("请先选择要删除的商户");
        return;
      }
      const merchants = this.selectedRows.slice();
      this.$confirm(`确定删除选中的 ${merchants.length} 个商户的全部价格明细吗？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          this.batchDeleteLoading = true;
          return findPriceBookMerchantDetails({
            priceBookId: this.priceBook.id,
            merchantIds: merchants.map((row) => row.merchantId).join(","),
          });
        })
        .then((res) => {
          if (res.code !== "200") {
            this.$message.error(res.message || "加载商户价明细失败");
            return;
          }
          const data = this.parseData(res);
          this.doDelete(this.applyPendingToRows(Array.isArray(data) ? data : []));
        })
        .catch((err) => {
          if (err !== "cancel" && err !== "close") {
            console.error("MerchantPriceDrawer handleBatchDelete error:", err);
            this.$message.error("批量删除商户价异常");
          }
        })
        .finally(() => {
          this.batchDeleteLoading = false;
        });
    },
    handleDetailBatchDelete() {
      if (!this.detailSelectedRows.length) {
        this.$message.warning("请先选择要删除的价格明细");
        return;
      }
      const rows = this.detailSelectedRows.slice();
      this.$confirm(`确定删除选中的 ${rows.length} 条价格明细吗？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => this.doDelete(rows))
        .catch(() => {});
    },
    doDelete(rows) {
      const targets = (rows || []).filter(
        (row) => row && row.id && !row.pendingApproval && row.pendingAction !== "DELETE"
      );
      const skipped = (rows || []).length - targets.length;
      if (!targets.length) {
        this.$message.warning("所选明细正在审批中或已暂存删除，不能重复删除");
        return;
      }
      const changes = targets.map((row) => ({
        action: "DELETE",
        merchantPriceId: row.id,
        merchantId: row.merchantId,
        merchantName: row.merchantName,
        merchantPhone: row.merchantPhone,
        goodsFilterName: row.goodsFilterName,
        pricingItemCode: row.pricingItemCode,
        pricingItemName: row.pricingItemName,
        oldPrice: row.price,
      }));
      this.queuePendingChanges(changes);
      targets.forEach((row) => {
        this.$set(row, "pendingAction", "DELETE");
      });
      this.selectedRows = [];
      this.detailSelectedRows = [];
      this.$message.success(
        `已暂存删除 ${targets.length} 条，提交审核后生效${skipped ? `；已跳过 ${skipped} 条不可操作明细` : ""}`
      );
      this.handleList();
      if (this.detailDialogVisible) this.loadMerchantDetails();
    },
    handleClosed() {
      this.tableData = [];
      this.selectedRows = [];
      this.detailDialogVisible = false;
      this.detailMerchant = null;
      this.detailRows = [];
      this.detailSelectedRows = [];
      this.searchName = "";
      this.pendingDialogVisible = false;
      this.pendingSelectedRows = [];
      this.page = { total: 0, currentPage: 1, pageSize: 10 };
      this.$emit("close");
    },
    /* ============ Excel 批量导入（三步） ============ */
    // 第 1 步：下载模板
    handleDownloadTemplate() {
      if (!this.priceBook) return;
      this.templateLoading = true;
      downloadMerchantImportTemplate(this.priceBook.id)
        .then((response) => {
          let filename = `商户价导入模板-${this.priceBook.name}.xlsx`;
          const disposition = response.headers && response.headers["content-disposition"];
          if (disposition) {
            const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
            if (match && match[1]) filename = decodeURI(match[1].replace(/"/g, ""));
          }
          const blob = new Blob([response.data], { type: "application/octet-stream" });
          if (typeof window.navigator.msSaveBlob !== "undefined") {
            window.navigator.msSaveBlob(blob, filename);
          } else {
            const blobURL = window.URL.createObjectURL(blob);
            const tempLink = document.createElement("a");
            tempLink.style.display = "none";
            tempLink.href = blobURL;
            tempLink.setAttribute("download", filename);
            if (typeof tempLink.download === "undefined") {
              tempLink.setAttribute("target", "_blank");
            }
            document.body.appendChild(tempLink);
            tempLink.click();
            document.body.removeChild(tempLink);
            window.URL.revokeObjectURL(blobURL);
          }
        })
        .catch((err) => {
          console.log("handleDownloadTemplate error:", err);
          this.$message.error("模板下载失败");
        })
        .finally(() => {
          this.templateLoading = false;
        });
    },
    handleOpenImport() {
      this.importMode = "INSERT";
      this.importFile = null;
      this.importFileList = [];
      this.previewResult = null;
      this.importDialogVisible = true;
    },
    handleImportFileChange(file, fileList) {
      this.importFile = file.raw;
      this.importFileList = fileList.slice(-1);
      this.previewResult = null;
    },
    handleImportFileRemove() {
      this.importFile = null;
      this.importFileList = [];
    },
    handleImportFileExceed(files) {
      // 超出 limit 时替换为新文件
      this.$refs.importUpload.clearFiles();
      this.$refs.importUpload.handleStart(files[0]);
    },
    // 第 2 步：上传预览
    handlePreview() {
      if (!this.importFile) {
        this.$message.warning("请先选择文件");
        return;
      }
      this.previewLoading = true;
      const formData = new FormData();
      formData.append("file", this.importFile);
      formData.append("priceBookId", this.priceBook.id);
      formData.append("mode", this.importMode);
      previewMerchantImport(formData)
        .then((res) => {
          if (res.code === "200") {
            this.previewResult = this.parseData(res);
          } else {
            this.previewResult = null;
            this.$message.error(res.message || "预览失败");
          }
        })
        .catch((err) => {
          console.log("handlePreview error:", err);
          this.$message.error("预览异常");
        })
        .finally(() => {
          this.previewLoading = false;
        });
    },
    previewRowClass({ row }) {
      return row.error ? "preview-error-row" : "";
    },
    // 第 3 步：确认导入（只回传校验通过的行）
    handleConfirmImport() {
      if (!this.validPreviewRows.length) return;
      this.confirmLoading = true;
      confirmMerchantImport({
        priceBookId: this.priceBook.id,
        mode: this.importMode,
        rows: this.validPreviewRows.map((r) => ({
          merchantName: r.merchantName,
          merchantPhone: r.merchantPhone || "",
          goodsFilterName: r.goodsFilterName || "",
          pricingItemCode: r.pricingItemCode || "BASE_AREA",
          pricingItemName: r.pricingItemName || "基础面积",
          price: r.price === "" || r.price == null ? null : r.price,
          subsidyPrice: this.isCeiling && (r.subsidyPrice === "" || r.subsidyPrice == null)
            ? null : (this.isCeiling ? r.subsidyPrice : null),
        })),
      })
        .then((res) => {
          if (res.code === "200") {
            const data = this.parseData(res);
            this.$message.success(`导入成功，共 ${data.imported != null ? data.imported : this.validPreviewRows.length} 条`);
            this.importDialogVisible = false;
            this.handleList();
          } else {
            this.$message.error(res.message || "导入失败");
          }
        })
        .catch((err) => {
          console.log("handleConfirmImport error:", err);
          this.$message.error("导入异常");
        })
        .finally(() => {
          this.confirmLoading = false;
        });
    },
    handleImportClosed() {
      this.importFile = null;
      this.importFileList = [];
      this.previewResult = null;
      this.$refs.importUpload && this.$refs.importUpload.clearFiles();
    },
  },
};
</script>

<style scoped>
.book-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.book-info-tip {
  font-size: 12px;
  color: #909399;
}
.filter-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.filter-bar-right {
  margin-left: auto;
  display: flex;
  gap: 8px;
}
.pager-bar {
  margin-top: 12px;
  text-align: right;
}
.detail-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.detail-merchant-info {
  display: flex;
  align-items: center;
  gap: 20px;
  color: #606266;
}
.pending-pagination {
  margin-top: 10px;
  text-align: right;
}
.price-text {
  color: #e6a23c;
  font-weight: 600;
}
.price-unit {
  color: #909399;
  font-size: 12px;
}
.item-code {
  display: block;
  color: #909399;
  font-size: 12px;
}
.empty-text {
  color: #c0c4cc;
}
.pricing-price-list {
  width: 100%;
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 6px 10px;
  box-sizing: border-box;
}
.goods-filter-select-line {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
}
.pricing-item-select-line {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
}
.merchant-filter-price-header {
  display: flex;
  justify-content: space-between;
  padding: 2px 0 6px;
  color: #909399;
  font-size: 12px;
}
.merchant-filter-price-header span:last-child {
  width: 150px;
  text-align: center;
}
.merchant-filter-price-header--double span:first-child {
  flex: 1;
  min-width: 0;
  text-align: left;
}
.merchant-filter-price-header--double span:not(:first-child) {
  flex: none;
  width: 140px;
  text-align: center;
}
.merchant-pricing-item-header {
  justify-content: initial;
}
.merchant-pricing-item-header span:first-child {
  flex: 1;
  min-width: 0;
  text-align: left;
}
.merchant-pricing-item-header span:nth-child(2) {
  flex: none;
  width: 150px;
  text-align: center;
}
.merchant-pricing-item-header .pricing-price-tip-placeholder {
  flex: none;
  width: 130px;
}
.merchant-filter-price-group + .merchant-filter-price-group {
  border-top: 1px solid #ebeef5;
  margin-top: 8px;
  padding-top: 8px;
}
.merchant-filter-price-group-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 32px;
  gap: 8px;
}
.merchant-filter-price-group-title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: #303133;
  font-weight: 600;
  line-height: 24px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.merchant-filter-group-toggle {
  flex: none;
  padding: 4px 0;
}
.merchant-filter-group-toggle i {
  margin-right: 2px;
}
.merchant-filter-price-name {
  line-height: 32px;
}
.merchant-filter-price-row .pricing-price-input {
  width: 140px;
}
.pricing-price-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
}
.pricing-price-row + .pricing-price-row {
  border-top: 1px solid #f2f6fc;
}
.pricing-price-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pricing-price-input {
  flex: none;
  width: 150px;
}
.pricing-price-tip {
  flex: none;
  color: #909399;
  font-size: 12px;
  white-space: nowrap;
}
.effective-price-info {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 4px;
}
.effective-price-source {
  color: #909399;
  font-size: 12px;
  white-space: nowrap;
}
.adjust-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  .adjust-price {
    color: #f56c6c;
    font-weight: 600;
  }
  .adjust-unit {
    color: #909399;
    font-size: 12px;
  }
  .adjust-tip {
    color: #909399;
    font-size: 12px;
  }
}
.import-steps {
  margin-bottom: 12px;
}
.import-step {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.step-num {
  flex: none;
  width: 20px;
  height: 20px;
  line-height: 20px;
  text-align: center;
  border-radius: 50%;
  background: #409eff;
  color: #fff;
  font-size: 12px;
}
.step-tip {
  font-size: 12px;
  color: #909399;
}
.error-text {
  color: #f56c6c;
}
::v-deep .preview-error-row {
  background: #fef0f0;
}
</style>
